import { useMutation, useQueryClient } from '@tanstack/react-query'
import ApiContext from '../../context/ApiContext'
import { useContext } from 'react'
import { getAuth } from 'firebase/auth'
import { toast } from 'sonner'
import AuthContext from '../../context/AuthContext'
import { getUpcomingInvoiceQueryKey } from '../queries/useUpcomingInvoice'
import { ProductsQueryKey } from '../queries/useProducts'

type CreateBillingSessionParams = {
	priceId: string
	successUrl: string
	cancelUrl: string
}
type CreateBillingSessionResponse = {
	url?: string
}

const useCreateBillingSession = () => {
	const auth = getAuth()
	const apiContext = useContext(ApiContext)
	const authContext = useContext(AuthContext)
	const queryClient = useQueryClient()

	const createBillingSession = async (params: CreateBillingSessionParams): Promise<CreateBillingSessionResponse> => {
		const idToken = await auth.currentUser?.getIdToken(true)
		const response = await fetch(`${apiContext.apiUrl}/companies/${authContext.company?.id}/billing-session`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${idToken}`,
			},
			body: JSON.stringify(params),
		})
		const text = await response.text()
		if (!response.ok) {
			let body: { code?: string; message?: string }
			try {
				body = JSON.parse(text) as { code?: string; message?: string }
			} catch {
				throw new Error(text)
			}
			const err = new Error(body.message ?? text) as Error & { code?: string }
			if (body.code) err.code = body.code
			throw err
		}
		return JSON.parse(text) as CreateBillingSessionResponse
	}

	const { mutateAsync: handleCreateBillingSession, isPending: loading } = useMutation<
		CreateBillingSessionResponse,
		Error,
		CreateBillingSessionParams
	>({
		mutationFn: createBillingSession,
		onSuccess: () => {
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: getUpcomingInvoiceQueryKey(authContext.company?.id),
					refetchType: 'all',
				}),
				queryClient.invalidateQueries({
					queryKey: ProductsQueryKey,
					refetchType: 'all',
				}),
			])
		},
		onError: (error: Error & { code?: string }) => {
			if (error.code === 'NO_ENABLED_SEATS') return
			toast.error('Something went wrong', {
				description: error.message,
			})
		},
	})

	return { handleCreateBillingSession, loading }
}

export default useCreateBillingSession
